import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { appStorage } from 'utils/appStorage';
import { useIsLoggedIn } from 'modules/Auth/hooks';
import { getDefaultLayout, ConfigureHomeCardType, HOME_PAGE_LAYOUT_STORAGE_KEY } from '../context';

const QUERY_KEY = 'HomePageLayout';

export function useHomePageLayoutData() {
  const queryClient = useQueryClient();
  const isLoggedIn = useIsLoggedIn();
  const defaultCards = getDefaultLayout(isLoggedIn);
  const combinedQueryKey = [QUERY_KEY, { isLoggedIn }];

  const { data: cards = defaultCards } = useQuery(
    combinedQueryKey,
    () => {
      let storedHomePageLayout = appStorage.get(HOME_PAGE_LAYOUT_STORAGE_KEY, defaultCards);

      if (needToReconcileLayout(defaultCards, storedHomePageLayout)) {
        storedHomePageLayout = reconcileDefaultCardsWithLocalStorageData(defaultCards, storedHomePageLayout);
      }

      return storedHomePageLayout;
    },
    {
      enabled: isLoggedIn,
    },
  );

  const { mutate: setCards } = useMutation(
    async (newCards: typeof defaultCards) => {
      return Promise.resolve(appStorage.set(HOME_PAGE_LAYOUT_STORAGE_KEY, newCards));
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(combinedQueryKey);
      },
    },
  );

  const setCardsCallback = useCallback(
    (clbck: (newCards: ConfigureHomeCardType[]) => ConfigureHomeCardType[]) => {
      const oldData = queryClient.getQueryData(combinedQueryKey);
      const newData = clbck(oldData as ConfigureHomeCardType[]);

      if (isLoggedIn) {
        setCards(newData);
      }
    },
    [queryClient, setCards, isLoggedIn],
  );

  return { cards, setCardsCallback };
}

/**
 * Cards to show on home page can change anytime. This function would reconcile the
 * user's preferences stored in LocalStorage with that of the list of home page cards
 * available i.e, keeping the visibility and order of the cards of chosen by user with
 * that of the default cards.
 *
 * Example scenario 1:
 * Default cards: [A, B, C, D, E, F, G]
 * User's stored cards: [E, B, F, C]
 * Reconciled cards: [A, E, B, D, F, C, G]
 *
 * We want to keep user's RELATIVE ordering and visibility of cards intact.
 *
 *
 * Example scenario 2:
 * Default cards: [B, C, D, E, F, G]
 * User's stored cards: [A, E, B, F, C]
 * Reconciled cards: [E, B, D, F, C, G]
 *
 * We SHOULD NOT show cards that are not part of the default cards.
 */
export function reconcileDefaultCardsWithLocalStorageData(
  defaultHomePageLayout: ConfigureHomeCardType[] = [],
  storedHomePageLayout: ConfigureHomeCardType[] = [],
) {
  const userPreferences = storedHomePageLayout
    // Remove any cards from user preferences that are not part of the default layout.
    .filter((card) => defaultHomePageLayout.find((defaultCard) => defaultCard.id === card.id))
    // Sort the stored layout by 'idx' to ensure the order is maintained.
    .sort((cardA, cardB) => cardA.idx - cardB.idx);

  // Fill an empty/null Array with the default cards which are not part of the user's preferences.
  // Thus ensuring the ordering of the default cards is maintained.
  let reconciledLayout = Array.from({ length: defaultHomePageLayout.length }).map((_, idx) => {
    const defaultCard = defaultHomePageLayout[idx];
    const userPreferencesForDefaultCard = userPreferences.find((card) => card.id === defaultCard.id);

    if (!userPreferencesForDefaultCard) {
      return defaultCard;
    }

    return null;
  });

  reconciledLayout = reconciledLayout.map((defaultCard) => {
    if (defaultCard) {
      return defaultCard;
    }

    // If the default card is not present, we take the next available user preference card.
    const nextUserPreferenceCard = userPreferences.shift();
    if (nextUserPreferenceCard) {
      return nextUserPreferenceCard;
    }

    // Edge case: Likely will never occur, but in case of an anomaly.
    return null;
  });

  // Reorder the reconciled layout.
  return (
    reconciledLayout
      // Filter out any null values that may have been added during the reconciliation.
      .filter((card) => card !== null)
      // Assign indices to the cards based on their order in the reconciled layout.
      .map((card, idx) => {
        card.idx = idx;
        card.configIdx = idx;
        return card;
      })
  );
}

/**
 * Checks if there's a mismatch between the card IDs in the default layout and the stored layout.
 */
export function needToReconcileLayout(
  defaultHomePageLayout: ConfigureHomeCardType[] = [],
  storedHomePageLayout: ConfigureHomeCardType[] = [],
): boolean {
  const storedHomePageLayoutIds = storedHomePageLayout.map((card) => card.id);
  const defaultHomePageLayoutIds = defaultHomePageLayout.map((card) => card.id);

  return (
    storedHomePageLayoutIds.length !== defaultHomePageLayoutIds.length ||
    !defaultHomePageLayoutIds.every((id) => storedHomePageLayoutIds.includes(id))
  );
}
